import React, { useState, useContext } from "react"
import axios from "axios"
import styled from "styled-components"
import RichContentTextBlock from "./richContentTextBlock"
import Img from "gatsby-image"
import {
  Box,
  Container,
  Grid,
  Heading,
} from "@theme-ui/components"
import customBorder from "../images/custom-border.svg"
import DesignTokens from "./designTokens"
import { LanguageContext } from "../locales/langContext"

const JobApplicationFormBlock = ({ title, description, image }) => {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [cvFile, setCvFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const t = useContext(LanguageContext)

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file && file.type === "application/pdf") {
      setCvFile(file)
    } else {
      alert("Please select a PDF file")
      event.target.value = ""
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)

    try {
      // Convert file to base64 for sending
      let cvFileData = null
      if (cvFile) {
        const reader = new FileReader()
        cvFileData = await new Promise((resolve) => {
          reader.onload = () => {
            const base64 = reader.result.split(',')[1]
            resolve({
              content: base64,
              filename: cvFile.name
            })
          }
          reader.readAsDataURL(cvFile)
        })
      }

      const data = {
        firstName,
        lastName,
        email,
        phone,
        cvFile: cvFileData,
        submittedAt: new Date().toISOString(),
      }

      await axios.post("/.netlify/functions/send-application", data)

      if (typeof window !== "undefined" && window.dataLayer !== undefined) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: "formSubmission",
          formType: "JobApplication",
        })
      }

      setSuccess(true)
      setLoading(false)
    } catch (error) {
      console.error("Error submitting application:", error)
      setLoading(false)
      alert("Error submitting application. Please try again.")
    }
  }

  return (
    <Box sx={{ backgroundColor: "#F3F6F8", paddingTop: ["3rem"], paddingBottom: ["4rem"], marginBottom: ["-48px"] }}>
      <Container variant="sm">
        <Box
          mb={4}
          sx={{
            paddingTop: 4,
            backgroundImage: `url(${customBorder})`,
            backgroundRepeat: "no-repeat",
          }}
        >
          {title && (
            <Heading sx={{ color: "primary", fontSize: 5, mb: [3, 4, 5, 6] }}>{title}</Heading>
          )}
          <Grid
            sx={{
              gridTemplateColumns: [
                "1fr",
                "1fr",
                image ? "1fr 1fr" : "1fr"
              ],
              gap: [3, 3, 4, "10rem"],
              img: {
                objectFit: "cover!important",
              },
            }}
          >
            <Box>
              {description && <RichContentTextBlock html={description} />}
            </Box>

            {image && (
              <Box
                sx={{
                  width: "100%",
                  ".gatsby-image-wrapper": {
                    width: "100%",
                    height: "auto",
                    minHeight: "250px",
                  },
                }}
              >
                <Img
                  fluid={image.fluid}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            )}
          </Grid>

          <JobApplicationForm onSubmit={handleSubmit}>
            <InputGroup>
              <FormSection>
                <label htmlFor="firstName">{t.firstName} *</label>
                <input
                  onChange={e => setFirstName(e.target.value)}
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={firstName}
                  required
                />
              </FormSection>
              <FormSection>
                <label htmlFor="lastName">{t.lastName} *</label>
                <input
                  onChange={e => setLastName(e.target.value)}
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={lastName}
                  required
                />
              </FormSection>
            </InputGroup>

            <InputGroup>
              <FormSection>
                <label htmlFor="email">Email *</label>
                <input
                  onChange={e => setEmail(e.target.value)}
                  type="email"
                  name="email"
                  id="email"
                  value={email}
                  required
                />
              </FormSection>
              <FormSection>
                <label htmlFor="phone">{t.phone} *</label>
                <input
                  onChange={e => setPhone(e.target.value)}
                  type="tel"
                  name="phone"
                  id="phone"
                  value={phone}
                  required
                />
              </FormSection>
            </InputGroup>

            <FormSection>
              <label htmlFor="cv">{t.uploadCV} *</label>
              <FileInputWrapper>
                <FileInput
                  type="file"
                  id="cv"
                  name="cv"
                  accept=".pdf"
                  onChange={handleFileChange}
                  required
                />
                <FileInputLabel htmlFor="cv">
                  {cvFile ? `${t.fileSelected}: ${cvFile.name}` : t.selectFile}
                </FileInputLabel>
              </FileInputWrapper>
            </FormSection>

            <FormSection>
              <p>{t.consentPolicy}</p>
            </FormSection>

            <Checkbox>
              <input type="checkbox" id="privacy" name="privacy" required />
              <label htmlFor="privacy">{t.consent}</label>
            </Checkbox>

            <SubmitButtonWrapper>
              {!success && (
                <input
                  type="submit"
                  value={loading ? `${t.loading}...` : t.sendApplication}
                />
              )}
              {success && <input type="submit" value={t.applicationSubmitted} disabled />}
            </SubmitButtonWrapper>
          </JobApplicationForm>
        </Box>
      </Container>
    </Box>
  )
}

const JobApplicationForm = styled.form`
  margin-top: 6rem;
  label {
    margin-bottom: 0.25rem;
    color: black;
    font-size: 14px;
    transition: color 0.3s ease;
  }
  input,
  textarea {
    padding: ${DesignTokens.spacing[3]};
    color: black;
    border: none;
    font-size: 16px;
    background: transparent;
    border-bottom: 1px solid ${DesignTokens.colors.primary[700]};
    outline: none;
    transition: border-bottom-color 0.3s ease;
    &:hover,
    &:focus {
      border-bottom: 1px solid ${DesignTokens.colors.primary[500]};
    }
  }
  input[type="submit"] {
    background: ${DesignTokens.colors.primary[500]};
    border: none;
    color: white;
    text-transform: uppercase;
    font-weight: bold;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
    padding-left: 2rem;
    padding-right: 2rem;
    cursor: pointer;
    width: auto;
    display: inline-block;
    &:hover,
    &:active {
      background: ${DesignTokens.colors.tertiary[500]};
    }
    &:disabled {
      background: #4caf50;
      cursor: default;
    }
  }
  & > * {
    margin-bottom: ${DesignTokens.spacing[4]};
    &:last-child {
      margin-bottom: 0;
    }
  }
`

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${DesignTokens.spacing[3]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  &:focus-within label {
    color: ${DesignTokens.colors.primary[500]};
  }
  p {
    margin-top: 24px;
    font-size: 12px;
    color: ${DesignTokens.colors.primary[300]};
  }
`

const FileInputWrapper = styled.div`
  position: relative;
  margin-top: 0.5rem;
`

const FileInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`

const FileInputLabel = styled.label`
  display: block;
  padding: ${DesignTokens.spacing[3]};
  background: #F3F6F8;
  border: 1px solid ${DesignTokens.colors.primary[700]};
  cursor: pointer;
  font-size: 16px;
  color: ${DesignTokens.colors.primary[500]};
  transition: border-color 0.3s ease, color 0.3s ease;
  &:hover {
    border-color: ${DesignTokens.colors.primary[500]};
  }
`

const SubmitButtonWrapper = styled.div`
  display: block;
  margin-bottom: ${DesignTokens.spacing[4]};
`

const Checkbox = styled.div`
  display: flex;
  flex-direction: row;
  input {
    margin-right: 0.5rem;
  }
  label {
    font-size: 14px;
    line-height: 1.1;
    margin-bottom: 0 !important;
  }
`

export default JobApplicationFormBlock